import { GridApi, GridOptions, GridReadyEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { observer } from "mobx-react"
import { Search } from "modules/common/components/form/Search"
import { LocalLoader } from "modules/common/components/loader/Loader"
import { GridTheme } from "modules/common/components/table/GridTheme"
import { MAX_INT } from "modules/common/constants"
import { routes } from "modules/common/routes"
import { DefaultAgGridOptions } from "modules/common/services/table/helpers";
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage"
import {
  PendingSignatureItemType,
  SignatureDtoType,
  UserPendingSignaturesStoreType,
} from "modules/main/models/order-content-task-signatures"
import { OrderTabs } from "modules/orders-manage/details/OrderDetailsTabsPage"
import { NotificationStoreType } from "modules/root/models/alerts"
import { agGridLocale } from "modules/root/services/locale"
import React from "react"
import styles from "./SignaturesList.module.scss"
import { columns } from "./columns"
import SignDialog from '../crypto/CryptoDialog'
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { OwnSignaturesFilesList } from './OwnSignaturesFilesList'
import { EmployeeSignatureFileType, EmployeeSignatureInfoType, EmployeeSignaturesListType } from './models/employee-signatures'
import VerifySignaturesDialog from '../crypto/VerifySignaturesDialog'
import { getSignerType, getSignerTypeDisplay } from 'modules/orders-manage/details/content-task/RowTask'

const TABLE_NAME = "UserPendingSignatures"
const sizeSaver = saveAgGridColumnSize(TABLE_NAME)

class SignaturesItems extends React.Component<SignaturesListProps, SignaturesListState> {
  private gridApi: GridApi | null = null;

  constructor(props: SignaturesListProps) {
    super(props)

    this.state = {
      showOwnFilesDialog: false,
      showSignDialog: false,
      selectedSignature: null,
      signaturesToSignAll: null,
      showVerifyDialog: false,
      selectedForVerify: null,
      selectedSignerId: null,
      selectedSignerName: null,
      allowAttach: false,
    }
  }

  async componentDidMount() {
    await this.loadData()
  }

  componentDidUpdate(prevProps: SignaturesListProps) {
    if (prevProps.orderId !== this.props.orderId || prevProps.contentGuid !== this.props.contentGuid) {
      this.loadData()
    }
  }

  loadData = async () => {
    await this.props.store.loadPendingSignatures({
      orderId: this.props.orderId,
      contentGuid: this.props.contentGuid,
    })
  };

  handleSignClick = (signature: PendingSignatureItemType) => {
    this.setState({
      showSignDialog: true,
      selectedSignature: signature,
      selectedSignerId: null,
      selectedSignerName: null,
      allowAttach: false,
    })
  };

  handleSignAllClick = () => {
    this.setState({
      showSignDialog: true,
      selectedSignature: null,
      signaturesToSignAll: this.props.store.filteredSignatures,
    })

  }

  openOwnFilesDialog = () => {
    this.setState({
      showOwnFilesDialog: true,
      selectedSignature: null,
    })

  }

  private options: GridOptions = DefaultAgGridOptions();
  private config = columns(TABLE_NAME, this.props.baseUrl, this.handleSignClick);

  // Метод, который будет вызван из Dialog после создания подписи
  handleSubmitSignature = async (
    signature: PendingSignatureItemType,
    thumbprint: string,
    signatureData: string,
    rowversion: number
  ) => {
    const isEmployeeSignature = !signature.orderId || signature.signatureSource === 'employee'
    if (isEmployeeSignature && this.props.ownUserSignaturesList) {
      await this.props.ownUserSignaturesList.submitSignature(signature, signatureData, rowversion, this.state.selectedSignerId)
    }
    else {
      await this.props.store.submitSignature(signature, signatureData, rowversion)
    }
  };

  // Прикрепление внешнего файла подписи из окна подписания («Мои файлы»)
  handleAttachSignature = async (
    signature: PendingSignatureItemType,
    signatureData: string,
    signerNames: string[]
  ) => {
    if (!this.props.ownUserSignaturesList) {
      throw new Error('Прикрепление файла подписи доступно только для файлов из «Моих файлов»')
    }

    await this.props.ownUserSignaturesList.attachSignature(
      signature,
      signatureData,
      this.state.selectedSignerId,
      signerNames
    )
  };

  handleGetSignature = async (id: string, signatureSource: string): Promise<SignatureDtoType> => {
    const res = await this.props.store.getSignature(id, signatureSource)
    if (!res) {
      throw new Error('Не удалось получить данные подписи')
    }
    return res
  }

  handleGetVerifySignature = async (): Promise<SignatureDtoType> => {
    const res = await this.props.store.getSignature(this.state.selectedForVerify.id, "employee")
    if (!res) {
      throw new Error('Не удалось получить данные подписи')
    }
    return res
  }

  handleSignFromOwnFiles = (file: EmployeeSignatureFileType) => {

    // Создаем PendingSignatureItemType
    const signature: PendingSignatureItemType = {
      signatureId: file.id,
      orderId: this.props.orderId || '',
      orderNumber: '',
      orderName: '',
      contentGuid: this.props.contentGuid || '',
      contentName: '',
      version: file.version || 0,
      signerType: '',
      signerTypeDisplay: '',
      fileName: file.fileName,
      fileId: file.fileId,
      createdAt: file.date || '',
      date: file.date || '',
      signatureSource: 'employee', // Используем 'employee' для подписей из "Моих файлов"
      existingSignature: null
    }

    this.setState({
      showSignDialog: true,
      selectedSignature: signature,
      signaturesToSignAll: null,
      selectedSignerId: null,
      selectedSignerName: null,
      allowAttach: true,
    })
  }

  handleSignFromOwnFilesRole = (file: EmployeeSignatureFileType, employer: EmployeeSignatureInfoType) => {

    // Создаем PendingSignatureItemType
    const signature: PendingSignatureItemType = {
      signatureId: file.id,
      orderId: '',
      orderNumber: '',
      orderName: '',
      contentGuid: '',
      contentName: '',
      version: file.version || 0,
      signerType: getSignerType(employer.signerType),
      signerTypeDisplay: getSignerTypeDisplay(employer.signerType),
      fileName: file.fileName,
      fileId: file.fileId,
      createdAt: file.date || '',
      date: file.date || '',
      signatureSource: 'employee', // Используем 'employee' для подписей из "Моих файлов"
      existingSignature: null
    }

    // Владелец файла может подписать за назначенного подписанта
    const forAnother = employer.employerId !== this.props.userId

    this.setState({
      showSignDialog: true,
      selectedSignature: signature,
      signaturesToSignAll: null,
      selectedSignerId: forAnother ? employer.employerId : null,
      selectedSignerName: forAnother ? employer.employerName : null,
      allowAttach: true,
    })
  }

  // Метод для показа ошибок из Dialog
  handleNotifyError = (error: string) => {
    this.props.store.notify.error(error)
  };

  handleNotifySuccess = (success: string) => {
    this.props.store.notify.success(success)
  };

  closeSignDialog = () => {
    this.setState({
      showSignDialog: false,
      selectedSignature: null,
      signaturesToSignAll: null,
      selectedSignerId: null,
      selectedSignerName: null,
      allowAttach: false,
    })
  };

  closeOwnFilesDialog = () => {
    this.setState({
      showOwnFilesDialog: false,
      selectedSignature: null,
      signaturesToSignAll: null,
    })
    this.loadData()
  };

  onRowSelected = ({ data }: RowDoubleClickedEvent) => {
    // раздел и файл: вкладка выберет раздел и подсветит блок файла
    const file = data.fileId ? `&fileId=${data.fileId}` : ""
    const path = routes.orders.details(data.orderId, OrderTabs.project) + `&rowId=${data.contentGuid}` + file
    window.open(path, "_blank")
  };

  handleVerifyClick = (signature: EmployeeSignatureFileType) => {
    this.setState({
      showVerifyDialog: true,
      selectedForVerify: signature,
    })
  }

  closeVerifyDialog = () => {
    this.setState({
      showVerifyDialog: false,
      selectedForVerify: null,
    })
  }

  render() {
    const { loading, filteredSignatures, pendingSignatures } = this.props.store
    const { baseUrl, ownUserSignaturesList, userId } = this.props
    const { showSignDialog, selectedSignature, showOwnFilesDialog, showVerifyDialog, selectedForVerify } = this.state

    return (
      <div className={`${styles.wrapper}`}>
        <SignDialog
          isOpen={showSignDialog}
          selectedSignature={selectedSignature}
          onClose={this.closeSignDialog}
          onGetSignature={this.handleGetSignature}
          onSubmitSignature={this.handleSubmitSignature}
          onNotifyError={this.handleNotifyError}
          baseUrl={this.props.baseUrl}
          signaturesToSignAll={this.state.signaturesToSignAll}
          onNotifySuccess={this.handleNotifySuccess}
          selectedSignerName={this.state.selectedSignerName}
          allowAttach={this.state.allowAttach}
          onAttachSignature={this.handleAttachSignature}
        />

        {ownUserSignaturesList && showOwnFilesDialog &&
          <OwnSignaturesFilesList
            baseUrl={baseUrl}
            isOpen={showOwnFilesDialog}
            onClose={this.closeOwnFilesDialog}
            onVerifySignature={this.handleVerifyClick}
            store={ownUserSignaturesList}
            userId={userId}
            onSignFile={this.handleSignFromOwnFiles}
            onSignRoleFile={this.handleSignFromOwnFilesRole}
          />}
        <VerifySignaturesDialog
          isOpen={showVerifyDialog}
          signature={selectedForVerify}
          onClose={this.closeVerifyDialog}
          baseUrl={this.props.baseUrl}
          onGetSignature={this.handleGetVerifySignature}  // Тот же метод, что и для SignDialog
          onNotifyError={this.props.store.notify.error}
          onNotifySuccess={this.props.store.notify.success}
        />
        {loading ? (
          <div>
            <LocalLoader active={loading} />
          </div>
        ) : (
          <div>
            <div className={styles.header}>
              <div style={{ width: "100%", display: "flex", gap: "10px", alignItems: "center", justifyContent: "space-between" }}>
                <Search
                  query={this.props.store.query}
                  onSearch={this.props.store.setQuery}
                  style={{ background: "#fff", width: "850px", }}
                  placeholder="Поиск по договорам, разделам, файлам..."
                />
                {ownUserSignaturesList && <PlanrButton
                  type="blueish"
                  onClick={this.openOwnFilesDialog}
                  style={{ whiteSpace: "nowrap", marginLeft: "15px" }}
                >
                  Мои файлы
                </PlanrButton>}
                {this.props.store.filteredSignatures && this.props.store.filteredSignatures.length > 0 && (
                  <PlanrButton
                    type="greenish"
                    onClick={this.handleSignAllClick}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Подписать все ({this.props.store.filteredSignatures.length})
                  </PlanrButton>
                )}
              </div>
            </div>

            {filteredSignatures.length > 0 ? (
              <div className={styles.tableContainer}>
                <GridTheme bordered={true} className={`designed ${styles.table}`}>
                  <AgGridReact
                    rowStyle={{
                      fontFamily: "GothamPro, Arial, sans-serif",
                      color: "#00273D",
                    }}
                    rowHeight={40}
                    rowSelection="single"
                    suppressCellFocus={true}
                    enableCellTextSelection={true}
                    columnDefs={this.config}
                    rowData={filteredSignatures}
                    localeText={agGridLocale}
                    onRowDoubleClicked={this.onRowSelected}
                    onColumnResized={sizeSaver}
                    gridOptions={this.options}
                    rowBuffer={MAX_INT}
                    context={this}
                    onGridReady={this.onGridReady}
                  />
                </GridTheme>
              </div>
            ) : (
              <div className={styles.noData}>
                {pendingSignatures.length === 0
                  ? "Нет документов, ожидающих подписи"
                  : "Нет результатов по поиску"}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  onGridReady = async ({ api }: GridReadyEvent) => {
    this.gridApi = api
    this.gridApi.resetRowHeights()
    api.resetRowHeights()
  };
}

export const SignaturesList = observer(SignaturesItems)

interface SignaturesListProps {
  store: UserPendingSignaturesStoreType
  notification: NotificationStoreType
  baseUrl: string
  orderId?: string
  contentGuid?: string
  onSign?: (signature: PendingSignatureItemType) => Promise<void>
  ownUserSignaturesList?: EmployeeSignaturesListType
  userId: string
}

interface SignaturesListState {
  showSignDialog: boolean
  /** За кого подписываем из «Моих файлов» (null — за себя / без роли) */
  selectedSignerId: string | null
  selectedSignerName: string | null
  /** Показывать в окне подписания режим «Файлом подписи» */
  allowAttach: boolean
  showOwnFilesDialog: boolean
  selectedSignature: PendingSignatureItemType | null
  selectedForVerify: any | null
  showVerifyDialog: boolean
  signaturesToSignAll?: PendingSignatureItemType[] | null
}